// Builds the 856's Shipments panel: a generic Shipment/Order/Pack/Tare/Item
// tree built from each HL node's own children (set by lib/parser856.ts from
// HL01/HL02), not from a fixed nesting depth — so it reflects whatever
// structure BSN05 actually declares and wherever HL02 parent pointers
// actually put a Pack, Tare or Item.

import type {HLNode856, NameLoop856, Parsed856} from './parser856';
import {entriesOf} from './sections';
import type {Section, SectionGroup} from './sections';

const HL_KEYS_856: ReadonlyArray<
  Exclude<
    keyof HLNode856,
    'id' | 'parentId' | 'levelCode' | 'nameLoops' | 'children'
  >
> = [
  'hl',
  'td1',
  'td5',
  'refs',
  'per',
  'fob',
  'dtm',
  'prf',
  'man',
  'lin',
  'sn1',
  'pid',
  'other',
];

const NAME_LOOP_KEYS_856: ReadonlyArray<keyof NameLoop856> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
];

/** Builds one Shipment/Order/Pack/Tare/Item row, recursing into its HL
 * children (Order under Shipment, Pack/Tare/Item under Order, or wherever
 * else BSN05's declared structure and the actual HL02 pointers put them) so
 * the Shipments panel reflects the document's actual nesting regardless of
 * how deep BSN05's declared structure goes. */
function buildHLGroup856(node: HLNode856, path: string): SectionGroup {
  const entries = entriesOf(node, HL_KEYS_856, path);
  node.nameLoops.forEach((loop, i) => {
    entries.push(
      ...entriesOf(loop, NAME_LOOP_KEYS_856, `${path}.nameLoops[${i}]`)
    );
  });
  const subgroups = node.children.map((child, i) =>
    buildHLGroup856(child, `${path}.children[${i}]`)
  );
  return {
    key: path,
    entries,
    subgroups: subgroups.length > 0 ? subgroups : undefined,
  };
}

/** Builds the data-type sections for one 856 transaction: a non-repeating
 * shipment-details section (BSN), a repeating shipments section whose
 * groups are the root HL nodes (each nesting its own subgroups), and a
 * non-repeating totals section (CTT) — omitting any section with nothing
 * in it. */
export function buildSections856(parsed: Parsed856, txPath: string): Section[] {
  const sections: Section[] = [];

  const headerEntries = entriesOf(parsed.header, ['bsn'], `${txPath}.header`);
  if (headerEntries.length > 0) {
    sections.push({
      key: 'shipmentDetails',
      title: 'Shipment details',
      description:
        'Purpose code, shipment id/number, date and hierarchical structure code.',
      repeating: false,
      groups: [{key: `${txPath}.header`, entries: headerEntries}],
    });
  }

  const shipmentGroups = parsed.shipments.map((node, i) =>
    buildHLGroup856(node, `${txPath}.shipments[${i}]`)
  );
  if (shipmentGroups.length > 0) {
    sections.push({
      key: 'shipments',
      title: 'Shipments',
      description:
        'Each shipment, nesting its orders and the packs, tares and items beneath them.',
      repeating: true,
      groups: shipmentGroups,
    });
  }

  const totalsEntries = entriesOf(parsed.summary, ['ctt'], `${txPath}.summary`);
  if (totalsEntries.length > 0) {
    sections.push({
      key: 'totals',
      title: 'Totals',
      description: 'Line-item count.',
      repeating: false,
      groups: [{key: `${txPath}.summary`, entries: totalsEntries}],
    });
  }

  return sections;
}
